import React from 'react';
import { RefreshCcw } from 'lucide-react';

import IdentificationBadge from './IdentificationBadge';
import ManifestTruthCell from './ManifestTruthCell';
import PriceDistributionView from '../../components/PriceDistributionView';

const ExpandedRunCurrentRows = ({ controller, run, res, ridx, runMinConfidenceThreshold }) => {
  const {
    getFilteredItemEntries,
    getResultTokenCounts,
    getResultAttemptSourceInfo,
    getResultLotNumber,
    getResultImageUrls,
    getDisabledImageKeySet,
    normalizeImageKey,
    getLotActionKey,
    rerunningLotBusy,
    toFiniteNumber,
    computeAccuracyRatio,
    setSelectedImage,
    setSelectedImages,
    setSelectedLotNum,
    setSelectedItems,
    toggleLotImageDisabled,
    rerunSingleLot,
    getReviewNoteDraftKey,
    getTrustedReviewNote,
    reviewNoteDrafts,
    savingReviewNotes,
    setReviewNoteDrafts,
    saveReviewNote,
    lotNoteDrafts,
    setLotNoteDrafts,
    savingLotNotes,
    saveLotReviewNote,
    formatConfidencePercent,
    formatTokenCount,
    getSourceBadgeClassName,
    getSourceDisplayLabel,
    toFiniteCount,
    getItemIdentification,
    formatIdentificationTooltip,
  } = controller;

  const resultItemEntries = getFilteredItemEntries(res?.items, runMinConfidenceThreshold);
  if (resultItemEntries.length === 0) return null;

  const itemCount = resultItemEntries.length;
  const resultTokenTotals = getResultTokenCounts(res);
  const sourceInfo = getResultAttemptSourceInfo(res);
  const firstResultItem = (resultItemEntries.find((entry) => entry.item)?.item) || null;
  const lotNumber = getResultLotNumber(res, firstResultItem);
  const actionLotNumber = String(
    lotNumber ||
      getResultLotNumber(res) ||
      (firstResultItem ? getResultLotNumber({}, firstResultItem) : '') ||
      '',
  ).trim();
  const runId = String(run?.run_id || '').trim();
  const lotActionKey = getLotActionKey(runId, actionLotNumber || ridx);
  const isRerunningLot = !!rerunningLotBusy[lotActionKey];
  const resultImageUrls = getResultImageUrls(res, firstResultItem);
  const disabledImageKeySet = getDisabledImageKeySet(res);
  const activeImageKeySet = new Set(
    resultImageUrls
      .map((url) => normalizeImageKey(url) || String(url || '').trim().toLowerCase())
      .filter((imageKey) => imageKey && !disabledImageKeySet.has(imageKey)),
  );
  const visibleResultItemEntries = resultItemEntries.filter(
    (entry) => entry.item && typeof entry.item === 'object',
  );
  const visibleEstimatedSum = resultItemEntries.reduce(
    (sum, entry) =>
      entry.item && typeof entry.item === 'object'
        ? sum + (toFiniteNumber(entry?.item?.estimated) || 0)
        : sum,
    0,
  );
  const visibleLotActualValue =
    visibleResultItemEntries
      .map((entry) => toFiniteNumber(entry?.item?.actual))
      .find((value) => value !== null) ?? null;
  const activeImageCount = activeImageKeySet.size;
  const canRerunLot = Boolean(
    runId && actionLotNumber && resultImageUrls.length > 0 && activeImageCount > 0,
  );

  return (
    <React.Fragment>
      {resultItemEntries.map(({ item, originalIndex }, iidx) => {
        const rowItem = item && typeof item === 'object' ? item : {};
        const isFilteredOutRow = originalIndex === null;
        const hasConcreteItemIndex = Number.isFinite(Number(originalIndex));
        const rowLotNumber = getResultLotNumber(res, rowItem) || lotNumber;
        const imageUrlsForModal = getResultImageUrls(res, rowItem);
        const estimatedValue = toFiniteNumber(rowItem.estimated);
        const reviewNoteDraftKey = hasConcreteItemIndex
          ? getReviewNoteDraftKey({
              runId,
              lotNumber: rowLotNumber || lotNumber || ridx,
              resultIndex: ridx,
              itemIndex: originalIndex,
            })
          : '';
        const persistedReviewNote = hasConcreteItemIndex ? getTrustedReviewNote(rowItem) : '';
        const reviewNoteValue = hasConcreteItemIndex
          ? Object.prototype.hasOwnProperty.call(reviewNoteDrafts, reviewNoteDraftKey)
            ? reviewNoteDrafts[reviewNoteDraftKey]
            : persistedReviewNote
          : '';
        const isSavingReviewNote = hasConcreteItemIndex
          ? !!savingReviewNotes[reviewNoteDraftKey]
          : false;
        const hasReviewNoteChanges = hasConcreteItemIndex
          ? reviewNoteValue !== persistedReviewNote
          : false;
        const canSaveReviewNote = hasConcreteItemIndex && Boolean(runId && (rowLotNumber || lotNumber));
        const canSaveLotNote = Boolean(runId && (lotNumber || rowLotNumber || ridx));

        return (
          <tr key={`${ridx}-${originalIndex}`} className={`${iidx < itemCount - 1 ? '' : 'border-b border-hairline'} hover:bg-surface-2`}>
            {iidx === 0 && (
              <td className="px-1.5 py-1 border-r border-hairline align-top" rowSpan={itemCount}>
                <span>{rowLotNumber || '-'}</span>
              </td>
            )}
            {iidx === 0 && (
              <td className="px-1.5 py-1 border-r border-hairline align-top" rowSpan={itemCount}>
                <div className="flex flex-wrap gap-1">
                  {resultImageUrls.map((url, uidx) => {
                    const imageKey = normalizeImageKey(url) || String(url || '').trim().toLowerCase();
                    const isDisabledImage = disabledImageKeySet.has(imageKey);
                    const canToggleImage = Boolean(
                      runId && (actionLotNumber || rowLotNumber || lotNumber) && String(url || '').trim(),
                    );
                    return (
                      <div key={uidx} className="flex flex-col items-center gap-0.5">
                        <div className="relative">
                          <img
                            src={url}
                            alt={`${rowItem.item || rowItem.item_name || 'Lot image'} ${uidx + 1}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedImage(url);
                              setSelectedImages(imageUrlsForModal);
                              setSelectedLotNum(rowLotNumber);
                              setSelectedItems(res.items);
                            }}
                            className={`w-10 h-10 object-cover rounded shadow-sm hover:scale-[1.5] hover:z-10 transition-transform cursor-pointer ring-2 ${isDisabledImage ? 'grayscale opacity-35 ring-danger' : 'ring-positive'}`}
                          />
                          {isDisabledImage && (
                            <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-white bg-deep/60 rounded">
                              OFF
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            if (!canToggleImage) return;
                            toggleLotImageDisabled({
                              runId,
                              lotNumber: actionLotNumber || rowLotNumber || lotNumber,
                              imageUrl: url,
                              disable: !isDisabledImage,
                              resultIndex: ridx,
                            });
                          }}
                          disabled={!canToggleImage || isRerunningLot}
                          className={`rounded border px-1 py-0.5 text-[9px] leading-none font-semibold transition-colors ${!canToggleImage || isRerunningLot ? 'border-hairline bg-surface-2 text-muted cursor-not-allowed' : 'border-hairline bg-surface text-muted hover:bg-surface-2'}`}
                          title={isDisabledImage ? 'Turn on image for this lot' : 'Turn off image for this lot'}
                        >
                          {isDisabledImage ? 'On' : 'Off'}
                        </button>
                      </div>
                    );
                  })}
                  {resultImageUrls.length === 0 && (
                    <div className="w-10 h-10 bg-surface-2 rounded flex items-center justify-center text-[10px] text-muted">
                      No Img
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1 mt-2">
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      rerunSingleLot({
                        runId,
                        lotNumber: actionLotNumber || rowLotNumber || lotNumber,
                      });
                    }}
                    disabled={!canRerunLot || isRerunningLot}
                    className={`inline-flex items-center rounded border px-2 py-1 text-[10px] font-semibold transition-colors ${!canRerunLot || isRerunningLot ? 'border-hairline bg-surface-2 text-muted cursor-not-allowed' : 'border-accent/30 bg-accent/10 text-accent hover:bg-accent/20'}`}
                    title={
                      !runId
                        ? 'This run has no run_id, rerun is unavailable.'
                        : !(actionLotNumber || lotNumber)
                          ? 'Missing lot number.'
                          : activeImageCount <= 0
                            ? 'Enable at least one image before rerun.'
                            : 'Rerun this lot with enabled images only'
                    }
                  >
                    <RefreshCcw size={10} className={`mr-1 ${isRerunningLot ? 'animate-spin' : ''}`} />
                    {isRerunningLot ? 'Rerunning...' : 'Rerun lot'}
                  </button>
                  {Array.isArray(res.rerun_history) && res.rerun_history.length > 0 && (
                    <span
                      className="inline-flex items-center rounded border border-ai/30 bg-ai/10 text-ai px-2 py-1 text-[10px] font-semibold whitespace-nowrap"
                      title="Previous image-combination rerun attempts shown below"
                    >
                      Hist ({res.rerun_history.length})
                    </span>
                  )}
                </div>
              </td>
            )}

            {iidx === 0 && <ManifestTruthCell result={res} run={run} rowSpan={itemCount} />}
            <td className="px-1.5 py-1 border-r border-hairline font-medium">
              {isFilteredOutRow ? (
                <span className="text-muted">-</span>
              ) : (
                <div className="space-y-1">
                  <div className="break-words" title={rowItem.item || rowItem.item_name || '-'}>
                    {rowItem.item || rowItem.item_name || '-'}
                  </div>
                  <IdentificationBadge
                    identification={getItemIdentification(rowItem, res, run)}
                    formatTooltip={formatIdentificationTooltip}
                  />
                </div>
              )}
            </td>
            <td className="px-1.5 py-1 border-r border-hairline">
              {isFilteredOutRow ? <span className="text-muted">-</span> : estimatedValue === null ? '-' : `$${estimatedValue.toFixed(2)}`}
            </td>
            {iidx === 0 && (
              <td className="px-1.5 py-1 border-r border-hairline align-top font-bold" rowSpan={itemCount}>
                ${visibleEstimatedSum.toFixed(2)}
              </td>
            )}
            {iidx === 0 && (
              <td className="px-1.5 py-1 border-r border-hairline align-top" rowSpan={itemCount}>
                {visibleLotActualValue === null ? '-' : `$${visibleLotActualValue.toFixed(2)}`}
              </td>
            )}

            <td className="px-1.5 py-1 border-r border-hairline text-muted">
              {isFilteredOutRow ? (
                <div className="text-[10px] text-muted italic">Filtered out by confidence</div>
              ) : (
                (() => {
                  const condition = rowItem?.condition || 'Unknown';
                  const priceSource = rowItem?.price_source || 'model_knowledge';
                  const confidenceLabel = formatConfidencePercent(rowItem?.confidence_score, '-');
                  const sourceLabel = priceSource === 'ebay_rag' ? 'eBay RAG' : priceSource === 'image' ? 'Image' : 'Model Knowledge';
                  const sourceBg = priceSource === 'ebay_rag' ? 'bg-teal/10 text-teal' : priceSource === 'image' ? 'bg-warn/10 text-warn' : 'bg-surface-2 text-muted';
                  const condBg = condition === 'New' ? 'bg-positive/10 text-positive' : condition === 'Damaged' || condition === 'For Parts' ? 'bg-danger/10 text-danger' : condition === 'Unknown' ? 'bg-surface-2 text-muted' : 'bg-warn/10 text-warn';
                  return (
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1"><span className="text-[8px] text-muted">Condition:</span><span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${condBg}`}>{condition}</span></div>
                      <div className="flex items-center gap-1"><span className="text-[8px] text-muted">Price Src:</span><span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${sourceBg}`}>{sourceLabel}</span></div>
                      <div className="flex items-center gap-1"><span className="text-[8px] text-muted">Conf:</span><span className="inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold bg-ai/10 text-ai">{confidenceLabel}</span></div>
                      <PriceDistributionView rangeValue={rowItem?.estimated_value_range} distributionValue={rowItem?.price_estimate_distribution} maxBins={Number.MAX_SAFE_INTEGER} compact className="mt-0.5" />
                    </div>
                  );
                })()
              )}
            </td>

            <td className="px-1.5 py-1 border-r border-hairline align-top">
              {isFilteredOutRow ? (
                <span className="text-[10px] text-muted">-</span>
              ) : (
                <div className="space-y-1">
                  <textarea value={reviewNoteValue} onChange={(event) => { const nextValue = event.target.value.slice(0, 4000); setReviewNoteDrafts((previous) => ({ ...previous, [reviewNoteDraftKey]: nextValue })); }} maxLength={4000} placeholder="Add subitem note..." className="w-full min-h-[48px] rounded border border-hairline px-1.5 py-1 text-[10px] outline-none focus:border-accent text-ink placeholder:text-muted" />
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-muted">{reviewNoteValue.length}/4000</span>
                    <button type="button" onClick={(event) => { event.stopPropagation(); if (!canSaveReviewNote || isSavingReviewNote || !hasReviewNoteChanges) return; saveReviewNote({ runId, lotNumber: rowLotNumber || lotNumber, resultIndex: ridx, itemIndex: originalIndex, reviewNote: reviewNoteValue }); }} disabled={!canSaveReviewNote || isSavingReviewNote || !hasReviewNoteChanges} className={`rounded px-2 py-0.5 text-[10px] font-semibold transition-colors ${!canSaveReviewNote || isSavingReviewNote || !hasReviewNoteChanges ? 'bg-surface-2 text-muted cursor-not-allowed border border-hairline' : 'bg-brand text-white hover:bg-brand-hover border border-accent'}`}>{isSavingReviewNote ? 'Saving...' : 'Save Note'}</button>
                  </div>
                </div>
              )}
            </td>

            {iidx === 0 && (() => {
              const lotDraftKey = `lot-${runId}-${lotNumber || ridx}-${ridx}`;
              const persistedLotNote = typeof res.lot_review_note === 'string' ? res.lot_review_note : '';
              const lotNoteValue = Object.prototype.hasOwnProperty.call(lotNoteDrafts, lotDraftKey) ? lotNoteDrafts[lotDraftKey] : persistedLotNote;
              const isSavingLotNote = !!savingLotNotes[lotDraftKey];
              const hasLotNoteChanges = lotNoteValue !== persistedLotNote;
              return (
                <td className="px-1.5 py-1 border-r border-hairline align-top" rowSpan={itemCount}>
                  <div className="space-y-1">
                    <textarea value={lotNoteValue} onChange={(e) => { const v = e.target.value.slice(0, 4000); setLotNoteDrafts((prev) => ({ ...prev, [lotDraftKey]: v })); }} maxLength={4000} placeholder="Add item note..." className="w-full min-h-[48px] rounded border border-hairline px-1.5 py-1 text-[10px] outline-none focus:border-positive text-ink placeholder:text-muted" />
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-muted">{lotNoteValue.length}/4000</span>
                      <button type="button" onClick={(e) => { e.stopPropagation(); if (!canSaveLotNote || isSavingLotNote || !hasLotNoteChanges) return; saveLotReviewNote({ runId, lotNumber: lotNumber || ridx, resultIndex: ridx, reviewNote: lotNoteValue }); }} disabled={!canSaveLotNote || isSavingLotNote || !hasLotNoteChanges} className={`rounded px-2 py-0.5 text-[10px] font-semibold transition-colors ${!canSaveLotNote || isSavingLotNote || !hasLotNoteChanges ? 'bg-surface-2 text-muted cursor-not-allowed border border-hairline' : 'bg-positive/10 text-white hover:bg-positive/90 border border-positive'}`}>{isSavingLotNote ? 'Saving...' : 'Save'}</button>
                    </div>
                  </div>
                </td>
              );
            })()}

            {iidx === 0 && (() => {
              const aiSum = visibleEstimatedSum;
              const actual = visibleLotActualValue;
              const acc = computeAccuracyRatio(actual, aiSum);
              return (
                <td className="px-1.5 py-1 border-r border-hairline align-top" rowSpan={itemCount}>
                  {acc === null ? '-' : <span className={`px-1 py-0.5 rounded font-bold text-[10px] ${acc > 0.8 ? 'bg-positive/10 text-positive' : acc > 0.5 ? 'bg-warn/10 text-warn' : 'bg-danger/10 text-danger'}`}>{(acc * 100).toFixed(1)}%</span>}
                </td>
              );
            })()}
            {iidx === 0 && (
              <td className="px-1.5 py-1 border-r border-hairline align-top" rowSpan={itemCount}>
                {(() => {
                  const durSec = toFiniteNumber(res.duration);
                  const durMs = toFiniteNumber(res.duration_ms);
                  const dur = durMs > 0 ? durMs / 1000 : durSec > 0 ? durSec : 0;
                  const liveDur = toFiniteNumber(res.last_live_duration);
                  const displayDur = dur > 0 ? dur : liveDur;
                  return displayDur != null && displayDur > 0 ? `${displayDur.toFixed(1)}s` : '0ms';
                })()}
              </td>
            )}
            {iidx === 0 && (
              <td className="px-1.5 py-1 border-r border-hairline align-top" rowSpan={itemCount}>
                <span className={`inline-flex rounded px-1 py-0.5 text-[9px] font-semibold whitespace-nowrap ${getSourceBadgeClassName(sourceInfo.finalSource, sourceInfo.finalCacheHit)}`} title={sourceInfo.cacheKey || undefined}>
                  {sourceInfo.finalCacheHit === null ? getSourceDisplayLabel(sourceInfo.finalSource) : sourceInfo.finalCacheHit ? 'CACHE' : 'LIVE'}
                </span>
              </td>
            )}
            {iidx === 0 && (
              <td className="px-1.5 py-1 border-r border-hairline align-top" rowSpan={itemCount}>
                <span className={`inline-flex rounded px-1.5 py-0.5 text-[9px] font-semibold ${toFiniteCount(resultTokenTotals.input) !== null ? 'bg-teal/10 text-teal' : 'bg-surface-2 text-muted'}`}>{formatTokenCount(resultTokenTotals.input)}</span>
              </td>
            )}
            {iidx === 0 && (
              <td className="px-1.5 py-1 border-r border-hairline align-top" rowSpan={itemCount}>
                <span className={`inline-flex rounded px-1.5 py-0.5 text-[9px] font-semibold ${toFiniteCount(resultTokenTotals.output) !== null ? 'bg-ai/10 text-ai' : 'bg-surface-2 text-muted'}`}>{formatTokenCount(resultTokenTotals.output)}</span>
              </td>
            )}
            {iidx === 0 && (
              <td className="px-1.5 py-1 align-top" rowSpan={itemCount}>
                {controller.formatCostDisplay(res.cost)}
              </td>
            )}
          </tr>
        );
      })}
    </React.Fragment>
  );
};

export default ExpandedRunCurrentRows;
